/**
 * VoiceNotes Component
 * 
 * Record, play, and manage voice memos for an opportunity.
 * Tap-and-hold style recording with visual feedback.
 */

import React, { useCallback, useState } from 'react';
import { View, Text, Pressable, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { color, icon } from '../../../shared/tokens/mobile';
import { useVoiceNotes } from '../../../hooks/useVoiceNotes';
import { VoiceNote } from '../../../shared/utils/api';
import { styles } from './voiceNotesStyles';

interface VoiceNotesProps {
  portfolioId: number;
  opportunityId: number;
}

function formatDuration(ms: number | null): string {
  if (!ms) return '0:00';
  const totalSec = Math.round(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${sec.toString().padStart(2, '0')}`;
}

function formatTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function NoteCard({ note, isPlaying, onPlay, onStop, onDelete }: {
  note: VoiceNote;
  isPlaying: boolean;
  onPlay: () => void;
  onStop: () => void;
  onDelete: () => void;
}) {
  return (
    <View style={styles.noteCard}>
      <Pressable
        style={[styles.playButton, isPlaying && styles.playButtonActive]}
        onPress={isPlaying ? onStop : onPlay}
        accessibilityLabel={isPlaying ? 'Stop playback' : 'Play voice note'}
        accessibilityRole="button"
      >
        <Ionicons
          name={isPlaying ? 'stop' : 'play'}
          size={icon.md}
          color={isPlaying ? color.surface : color.primary}
        />
      </Pressable>

      <View style={styles.noteInfo}>
        <View style={styles.noteTopRow}>
          <Text style={styles.noteDuration}>{formatDuration(note.duration_ms)}</Text>
          {note.latitude != null && (
            <View style={styles.geoBadge}>
              <Ionicons name="location" size={10} color={color.success} />
            </View>
          )}
        </View>
        <Text style={styles.noteTime}>
          {note.recorder_name || 'Unknown'} • {formatTimeAgo(note.created_at)}
        </Text>
        {note.caption && (
          <Text style={styles.noteCaption} numberOfLines={1}>{note.caption}</Text>
        )}
      </View>

      <Pressable
        style={styles.deleteButton}
        onPress={onDelete}
        accessibilityLabel="Delete voice note"
        accessibilityRole="button"
      >
        <Ionicons name="trash-outline" size={icon.sm} color={color.textMuted} />
      </Pressable>
    </View>
  );
}

export default function VoiceNotes({ portfolioId, opportunityId }: VoiceNotesProps) {
  const {
    notes,
    loading,
    recording,
    playing,
    uploading,
    error,
    recordingDuration,
    startRecording,
    stopRecording,
    playNote,
    stopPlayback,
    deleteNote,
  } = useVoiceNotes({ portfolioId, opportunityId });

  const [expanded, setExpanded] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteConfirm = useCallback(async () => {
    if (confirmDeleteId == null) return;
    setDeleting(true);
    try {
      await deleteNote(confirmDeleteId);
    } finally {
      setDeleting(false);
      setConfirmDeleteId(null);
    }
  }, [confirmDeleteId, deleteNote]);

  const displayNotes = expanded ? notes : notes.slice(0, 3);

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Ionicons name="mic" size={icon.sm} color={color.primary} />
          <Text style={styles.headerTitle}>Voice Notes</Text>
          {notes.length > 0 && (
            <View style={styles.countBadge}>
              <Text style={styles.countText}>{notes.length}</Text>
            </View>
          )}
        </View>

        {/* Record button */}
        <Pressable
          style={[
            styles.recordButton,
            recording && styles.recordButtonActive,
            uploading && styles.recordButtonDisabled,
          ]}
          onPress={recording ? stopRecording : startRecording}
          disabled={uploading}
          accessibilityLabel={recording ? 'Stop recording' : 'Start recording'}
          accessibilityRole="button"
        >
          {uploading ? (
            <ActivityIndicator size="small" color={color.surface} />
          ) : recording ? (
            <>
              <View style={styles.recordDot} />
              <Text style={styles.recordTimerText}>
                {Math.floor(recordingDuration / 60)}:{(recordingDuration % 60).toString().padStart(2, '0')}
              </Text>
            </>
          ) : (
            <>
              <Ionicons name="mic" size={14} color={color.surface} />
              <Text style={styles.recordButtonText}>Record</Text>
            </>
          )}
        </Pressable>
      </View>

      {error && <Text style={styles.errorText}>{error}</Text>}

      {loading ? (
        <ActivityIndicator size="small" color={color.primary} style={styles.loader} />
      ) : notes.length === 0 ? (
        <Text style={styles.emptyText}>No voice notes yet. Tap Record to add one.</Text>
      ) : (
        <>
          {displayNotes.map(note => (
            <NoteCard
              key={note.id}
              note={note}
              isPlaying={playing === note.id}
              onPlay={() => playNote(note.id)}
              onStop={stopPlayback}
              onDelete={() => setConfirmDeleteId(note.id)}
            />
          ))}
          {notes.length > 3 && (
            <Pressable onPress={() => setExpanded(!expanded)} style={styles.showMore}>
              <Text style={styles.showMoreText}>
                {expanded ? 'Show less' : `Show all ${notes.length} notes`}
              </Text>
              <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={14} color={color.primary} />
            </Pressable>
          )}
        </>
      )}

      {/* Delete confirmation overlay */}
      {confirmDeleteId != null && (
        <View style={styles.confirmOverlay}>
          <View style={styles.confirmCard}>
            <Text style={styles.confirmTitle}>Delete Voice Note</Text>
            <Text style={styles.confirmMessage}>This recording will be permanently removed.</Text>
            <View style={styles.confirmActions}>
              <Pressable style={[styles.confirmBtn, styles.confirmCancelBtn]} onPress={() => setConfirmDeleteId(null)} disabled={deleting}>
                <Text style={styles.confirmCancelText}>Cancel</Text>
              </Pressable>
              <Pressable style={[styles.confirmBtn, styles.confirmDeleteBtn]} onPress={handleDeleteConfirm} disabled={deleting}>
                {deleting ? <ActivityIndicator size="small" color={color.surface} /> : <Text style={styles.confirmDeleteText}>Delete</Text>}
              </Pressable>
            </View>
          </View>
        </View>
      )}
    </View>
  );
}

